import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { InlineAlert } from '@/components/ui/InlineAlert'
import { Input } from '@/components/ui/Input'
import { PhoneInput } from '@/components/ui/MaskedInput'
import { useT } from '@/i18n'
import { passwordProblem } from '@/lib/password'
import { authService } from '@/services'
import { AuthLayout } from './AuthLayout'
import { PasswordInput } from './PasswordInput'

type Step = 'phone' | 'code' | 'password' | 'done'

export function PasswordResetPage() {
  const navigate = useNavigate()
  const t = useT()
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [challengeId, setChallengeId] = useState('')
  const [token, setToken] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendIn])

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      const apiError = toApiError(err)
      setError(
        apiError.status === 429
          ? apiError.retryAfter
            ? t('auth.reset.tooManyIn', { seconds: apiError.retryAfter })
            : t('auth.reset.tooManyLater')
          : apiError.status === 503
            ? t('auth.reset.smsUnavailable')
            : apiError.message,
      )
    } finally {
      setBusy(false)
    }
  }

  const requestCode = (event?: FormEvent) => {
    event?.preventDefault()
    if (!phone.trim()) return setError(t('auth.reset.enterPhone'))
    void run(async () => {
      const result = await authService.passwordReset.request(phone.trim())
      setChallengeId(result.challenge_id)
      setResendIn(result.resend_after_seconds || 60)
      setStep('code')
    })
  }

  const verifyCode = (event: FormEvent) => {
    event.preventDefault()
    if (!code.trim()) return setError(t('auth.reset.enterCode'))
    void run(async () => {
      const result = await authService.passwordReset.verify(challengeId, code.trim())
      setToken(result.verification_token)
      setStep('password')
    })
  }

  const confirmPassword = (event: FormEvent) => {
    event.preventDefault()
    const problem = passwordProblem(password, t)
    if (problem) return setError(problem)
    if (password !== confirm) return setError(t('validation.passwordMismatch'))
    void run(async () => {
      await authService.passwordReset.confirm(challengeId, token, password)
      setStep('done')
    })
  }

  const back = (
    <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-fg underline-offset-4 hover:underline">
      <ArrowLeft className="size-3.5" /> {t('auth.backToSignIn')}
    </Link>
  )

  if (step === 'done') {
    return (
      <AuthLayout title={t('auth.reset.doneTitle')} description={t('auth.reset.doneText')}>
        <div className="flex flex-col items-center gap-5 py-2">
          <CheckCircle2 className="size-10 text-success" strokeWidth={1.5} />
          <Button variant="primary" size="lg" className="w-full" onClick={() => navigate('/login', { replace: true })}>
            {t('auth.reset.continue')}
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={t('auth.reset.title')}
      description={
        step === 'phone'
          ? t('auth.reset.phoneStep')
          : step === 'code'
            ? t('auth.reset.codeStep', { phone })
            : t('auth.reset.passwordStep')
      }
      footer={back}
    >
      <ol className="mb-5 flex gap-1.5" aria-label={t('auth.reset.progress')}>
        {(['phone', 'code', 'password'] as Step[]).map((item, index) => (
          <li
            key={item}
            aria-current={item === step ? 'step' : undefined}
            className={`h-1 flex-1 rounded-full ${['phone', 'code', 'password'].indexOf(step) >= index ? 'bg-accent' : 'bg-hover'}`}
          />
        ))}
      </ol>

      {error && <InlineAlert tone="danger" className="mb-4">{error}</InlineAlert>}

      {step === 'phone' && (
        <form onSubmit={requestCode} noValidate className="flex flex-col gap-4">
          <Field label={t('auth.reset.phone')} required>
            <PhoneInput autoComplete="tel" autoFocus value={phone} onValueChange={setPhone} className="h-10" />
          </Field>
          <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
            {t('auth.reset.sendCode')}
          </Button>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={verifyCode} noValidate className="flex flex-col gap-4">
          <Field label={t('auth.reset.code')} required>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="num h-11 text-center text-[length:calc(18px*var(--text-scale))] tracking-[0.4em]"
              maxLength={8}
            />
          </Field>
          <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
            {t('auth.reset.verify')}
          </Button>
          <Button variant="ghost" disabled={resendIn > 0 || busy} onClick={() => requestCode()}>
            {resendIn > 0 ? t('auth.reset.resendIn', { seconds: resendIn }) : t('auth.reset.resend')}
          </Button>
        </form>
      )}

      {step === 'password' && (
        <form onSubmit={confirmPassword} noValidate className="flex flex-col gap-4">
          <Field label={t('auth.reset.newPassword')} required hint={t('validation.passwordRule')}>
            <PasswordInput autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label={t('auth.reset.repeatPassword')} required>
            <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
            {t('auth.reset.update')}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
